import {inject, Injectable} from '@angular/core';
import {
  createClient,
  isAuthApiError,
  isAuthSessionMissingError,
  type Factor,
  type Session,
  type SupabaseClient,
  type UserIdentity,
} from '@supabase/supabase-js';
import {RegionService} from './region.service';
import {StorageService} from './storage.service';
import {
  resolveSupabaseAuthEnv,
  supabaseAuthDev,
  supabaseAuthProd,
  type SupabaseAuthEnv,
} from '../supabase.env';
import {resolveAccountOAuthConfig} from '../features/account/oauth-callback/oauth-callback.constants';

const TERMINAL_REFRESH_ERROR_CODES = new Set([
  'invalid_grant',
  'refresh_token_already_used',
  'refresh_token_not_found',
  'session_expired',
  'session_not_found',
  'user_banned',
  'user_not_found',
]);

const OAUTH_REFRESH_SKEW_SECONDS = 60;

interface StoredOAuthSession {
  access_token: string;
  refresh_token: string;
  expires_at: number;
  user: Session['user'];
}

interface OAuthTokenResponse {
  access_token?: string;
  refresh_token?: string;
  expires_in?: number;
  error?: string;
  error_description?: string;
}

export class OAuthRefreshError extends Error {
  constructor(
    message: string,
    readonly code: string | undefined,
    readonly status: number,
  ) {
    super(message);
    this.name = 'OAuthRefreshError';
  }
}

export function isTerminalRefreshError(error: unknown): boolean {
  return isAuthSessionMissingError(error)
    // OAuth token 端点返回 400 基本就意味着 refresh_token 已失效，不再依赖 `code` 白名单精确匹配
    || (error instanceof OAuthRefreshError && (error.status === 400
      || Boolean(error.code && TERMINAL_REFRESH_ERROR_CODES.has(error.code))))
    || (isAuthApiError(error) && Boolean(error.code && TERMINAL_REFRESH_ERROR_CODES.has(error.code)));
}

@Injectable({
  providedIn: 'root',
})
export class SupabaseAuthService {
  private readonly regionService = inject(RegionService);
  private readonly storageService = inject(StorageService);

  private client: SupabaseClient | null = null;
  private clientKey: string | null = null;
  private initPromise: Promise<SupabaseClient> | null = null;
  private activeSession: Session | null = null;
  private refreshPromise: Promise<Session | null> | null = null;
  private sessionGeneration = 0;

  /** Drop in-memory client so the next call binds to the current region Auth env. */
  resetClient(): void {
    this.client = null;
    this.clientKey = null;
    this.initPromise = null;
    this.activeSession = null;
    this.refreshPromise = null;
    this.sessionGeneration += 1;
  }

  /** Resolved Auth project (url/publishableKey) for the current region — used to build OAuth Server URLs. */
  async getAuthEnv(): Promise<SupabaseAuthEnv> {
    const regionCode = await this.regionService.getRegionCode();
    return resolveSupabaseAuthEnv(regionCode);
  }

  async ensureClient(): Promise<SupabaseClient> {
    const regionCode = await this.regionService.getRegionCode();
    const env = resolveSupabaseAuthEnv(regionCode);
    const key = env.storageKey;

    if (this.client && this.clientKey === key) {
      return this.client;
    }

    if (this.initPromise && this.clientKey === key) {
      return this.initPromise;
    }

    this.clientKey = key;
    const createPromise = this.createClient(env);
    this.initPromise = createPromise;
    const client = await createPromise;
    if (this.initPromise === createPromise) {
      this.client = client;
      this.initPromise = null;
    }
    return client;
  }

  async adoptExternalSession(accessToken: string, refreshToken: string): Promise<Session> {
    const client = await this.ensureClient();
    const {data, error} = await client.auth.setSession({
      access_token: accessToken,
      refresh_token: refreshToken,
    });
    if (error || !data.session) {
      throw error ?? new Error('Failed to adopt external session.');
    }
    this.activeSession = data.session;
    await this.persistOAuthSession(data.session);
    return data.session;
  }

  async getSession(): Promise<Session | null> {
    const env = await this.getAuthEnv();
    let stored = await this.readOAuthSession(env);
    if (!stored) {
      stored = await this.migrateLegacySession(env);
    }
    if (!stored) {
      return null;
    }

    if (stored.expires_at <= this.nowSeconds() + OAUTH_REFRESH_SKEW_SECONDS) {
      return this.refreshOAuthSession(stored);
    }

    return this.hydrateClientSession(stored);
  }

  async getAccessToken(): Promise<string | null> {
    try {
      const session = await this.getSession();
      return session?.access_token ?? null;
    } catch (error) {
      if (!isTerminalRefreshError(error)) {
        throw error;
      }
      await this.clearPersistedSession();
      this.resetClient();
      return null;
    }
  }

  /** Server-validated Auth user (preferred over session cache for profile fields). */
  async getUser(): Promise<Session['user']> {
    if (!await this.getSession()) {
      throw new Error('Not signed in.');
    }
    const client = await this.ensureClient();
    const {data, error} = await client.auth.getUser();
    if (error || !data.user) {
      throw error ?? new Error('Failed to load user.');
    }
    return data.user;
  }

  async refreshSession(): Promise<Session | null> {
    const env = await this.getAuthEnv();
    const stored = await this.readOAuthSession(env) ?? await this.migrateLegacySession(env);
    if (!stored) {
      return null;
    }
    return this.refreshOAuthSession(stored);
  }

  async getUserIdentities(): Promise<UserIdentity[]> {
    if (!await this.getSession()) {
      return [];
    }
    const client = await this.ensureClient();
    const {data, error} = await client.auth.getUserIdentities();
    if (error) {
      throw error;
    }
    return data?.identities ?? [];
  }

  async listTotpFactors(): Promise<Factor<'totp', 'verified'>[]> {
    if (!await this.getSession()) {
      return [];
    }
    const client = await this.ensureClient();
    const {data, error} = await client.auth.mfa.listFactors();
    if (error) {
      throw error;
    }
    return data?.totp ?? [];
  }

  /** Used by security verification when the caller chooses MFA. */
  async challengeAndVerifyTotp(factorId: string, code: string): Promise<Session> {
    const currentSession = await this.getSession();
    if (!currentSession) {
      throw new Error('No active OAuth session.');
    }
    const client = await this.ensureClient();
    const {data, error} = await client.auth.mfa.challengeAndVerify({
      factorId,
      code: code.trim(),
    });
    if (error) {
      throw error;
    }
    // MFA verify refreshes the JWT inside the Supabase client (aal2). Keep the
    // OAuth-bound refresh token if the endpoint does not rotate it.
    const session: Session = {
      ...data,
      refresh_token: data.refresh_token || currentSession.refresh_token,
      expires_at: this.nowSeconds() + data.expires_in,
    };
    this.activeSession = session;
    await this.persistOAuthSession(session);
    return session;
  }

  async signOutCurrentSession(): Promise<void> {
    await this.clearPersistedSession();
    this.resetClient();
  }

  async signOutAllSessions(): Promise<void> {
    // Global account/session management belongs to web-mp-account. This app
    // owns only its OAuth client session, so both sign-out variants are local.
    await this.signOutCurrentSession();
  }

  private async createClient(env: SupabaseAuthEnv): Promise<SupabaseClient> {
    return createClient(env.url, env.publishableKey, {
      auth: {
        persistSession: false,
        autoRefreshToken: false,
        detectSessionInUrl: false,
        flowType: 'pkce',
      },
    });
  }

  private oauthStorageKey(env: SupabaseAuthEnv): string {
    return `${env.storageKey}-oauth-client`;
  }

  private async persistOAuthSession(session: Session): Promise<void> {
    const env = await this.getAuthEnv();
    const expiresAt = session.expires_at ?? this.jwtExpiry(session.access_token);
    if (!expiresAt) {
      throw new Error('OAuth access token has no expiry.');
    }
    const stored: StoredOAuthSession = {
      access_token: session.access_token,
      refresh_token: session.refresh_token,
      expires_at: expiresAt,
      user: session.user,
    };
    await this.storageService.lfSet(this.oauthStorageKey(env), stored);
    // Keep the legacy `token` key so MQTT / older readers see the active bearer.
    await this.storageService.lfSet('token', session.access_token);
    await this.storageService.lfSet('uid', session.user.id);
  }

  private async readOAuthSession(env: SupabaseAuthEnv): Promise<StoredOAuthSession | null> {
    return this.normalizeStoredSession(await this.storageService.lfGet(this.oauthStorageKey(env)));
  }

  private async migrateLegacySession(env: SupabaseAuthEnv): Promise<StoredOAuthSession | null> {
    const legacy = this.normalizeStoredSession(await this.storageService.lfGet(env.storageKey));
    if (!legacy) {
      return null;
    }
    await this.storageService.lfSet(this.oauthStorageKey(env), legacy);
    await this.storageService.lfRemove(env.storageKey);
    return legacy;
  }

  private normalizeStoredSession(value: unknown): StoredOAuthSession | null {
    let candidate = value;
    if (typeof candidate === 'string') {
      try {
        candidate = JSON.parse(candidate);
      } catch {
        return null;
      }
    }
    if (!candidate || typeof candidate !== 'object') {
      return null;
    }
    const record = candidate as Partial<StoredOAuthSession>;
    const expiresAt = typeof record.expires_at === 'number'
      ? record.expires_at
      : typeof record.access_token === 'string' ? this.jwtExpiry(record.access_token) : null;
    if (
      typeof record.access_token !== 'string'
      || typeof record.refresh_token !== 'string'
      || !expiresAt
      || !record.user
      || typeof record.user.id !== 'string'
    ) {
      return null;
    }
    return {
      access_token: record.access_token,
      refresh_token: record.refresh_token,
      expires_at: expiresAt,
      user: record.user,
    };
  }

  private async hydrateClientSession(stored: StoredOAuthSession): Promise<Session> {
    if (this.activeSession?.access_token === stored.access_token) {
      return this.activeSession;
    }
    const client = await this.ensureClient();
    const {data, error} = await client.auth.setSession({
      access_token: stored.access_token,
      refresh_token: stored.refresh_token,
    });
    if (error || !data.session) {
      throw error ?? new Error('Failed to hydrate OAuth session.');
    }
    this.activeSession = data.session;
    return data.session;
  }

  private refreshOAuthSession(stored: StoredOAuthSession): Promise<Session | null> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }
    const generation = this.sessionGeneration;
    const refreshPromise = this.withOAuthRefreshLock(stored, generation);
    this.refreshPromise = refreshPromise;
    void refreshPromise.finally(() => {
      if (this.refreshPromise === refreshPromise) {
        this.refreshPromise = null;
      }
    }).catch(() => undefined);
    return refreshPromise;
  }

  private async withOAuthRefreshLock(stored: StoredOAuthSession, generation: number): Promise<Session> {
    const env = await this.getAuthEnv();
    const refresh = async () => {
      const latest = await this.readOAuthSession(env);
      if (latest && latest.refresh_token !== stored.refresh_token) {
        return this.hydrateClientSession(latest);
      }
      return this.performOAuthRefresh(env, latest ?? stored, generation);
    };
    const locks = globalThis.navigator?.locks;
    if (locks) {
      return locks.request(`mp-oauth-refresh:${env.storageKey}`, refresh);
    }
    return refresh();
  }

  private async performOAuthRefresh(
    env: SupabaseAuthEnv,
    stored: StoredOAuthSession,
    generation: number,
  ): Promise<Session> {
    const response = await fetch(`${env.url}/auth/v1/oauth/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
        apikey: env.publishableKey,
      },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        refresh_token: stored.refresh_token,
        client_id: resolveAccountOAuthConfig(await this.regionService.getRegionCode()).oauthClientId,
      }).toString(),
    });
    const tokenData = await response.json() as OAuthTokenResponse;
    if (!response.ok || !tokenData.access_token) {
      throw new OAuthRefreshError(
        tokenData.error_description || tokenData.error || 'OAuth token refresh failed.',
        tokenData.error,
        response.status,
      );
    }

    const client = await this.ensureClient();
    const {data, error} = await client.auth.setSession({
      access_token: tokenData.access_token,
      refresh_token: tokenData.refresh_token || stored.refresh_token,
    });
    if (error || !data.session) {
      throw error ?? new Error('OAuth refresh returned an invalid session.');
    }
    if (generation !== this.sessionGeneration) {
      throw new Error('OAuth refresh was discarded because the local session changed.');
    }
    this.activeSession = data.session;
    await this.persistOAuthSession(data.session);
    return data.session;
  }

  private jwtExpiry(accessToken: string): number | null {
    try {
      const payload = accessToken.split('.')[1];
      if (!payload) {
        return null;
      }
      const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
      const decoded = JSON.parse(atob(normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '='))) as {exp?: unknown};
      return typeof decoded.exp === 'number' ? decoded.exp : null;
    } catch {
      return null;
    }
  }

  private nowSeconds(): number {
    return Math.floor(Date.now() / 1000);
  }

  private async clearPersistedSession(): Promise<void> {
    await this.storageService.lfRemove('token');
    await this.storageService.lfRemove('uid');
    // Clear both Auth project sessions so region switches cannot reuse the wrong JWT.
    await this.storageService.lfRemove(supabaseAuthDev.storageKey);
    await this.storageService.lfRemove(supabaseAuthProd.storageKey);
    await this.storageService.lfRemove(this.oauthStorageKey(supabaseAuthDev));
    await this.storageService.lfRemove(this.oauthStorageKey(supabaseAuthProd));
  }
}
